import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ConsultantPartnerRegisterPage from "./pages/ConsultantPartnerRegisterPage";
import AccountantPartnerRegisterPage from "./pages/AccountantPartnerRegisterPage";
import AccountantPartnerLandingPage from "./pages/AccountantPartnerLandingPage";
import ClientDashboard from "./pages/ClientDashboard";
import DiagnosticWizard from "./pages/DiagnosticWizard";
import DiagnosticResults from "./pages/DiagnosticResults";
import DiagnosticPaymentStatus from "./pages/DiagnosticPaymentStatus";
import AdminDashboard from "./pages/AdminDashboard";
import AdminConsultants from "./pages/AdminConsultants";
import AdminReferrals from "./pages/AdminReferrals";
import AdminCompanies from "./pages/AdminCompanies";
import AdminResults from "./pages/AdminResults";
import AdminConsultancyRequests from "./pages/AdminConsultancyRequests";
import AdminUsers from "./pages/AdminUsers";
import AdminCoupons from "./pages/AdminCoupons";
import AdminAccountants from "./pages/AdminAccountants";
import ConsultantDashboard from "./pages/ConsultantDashboard";
import AccountantDashboard from "./pages/AccountantDashboard";
import ProgrammerFinance from "./pages/ProgrammerFinance";
import ProgrammerAudit from "./pages/ProgrammerAudit";
import ProgrammerAnalytics from "./pages/ProgrammerAnalytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
const AUTH_TOKEN_KEY = "credita_auth_token";

const persistAuthToken = (token) => {
  if (!token) return;
  localStorage.setItem(AUTH_TOKEN_KEY, token);
};

const clearAuthToken = () => {
  localStorage.removeItem(AUTH_TOKEN_KEY);
};

const getStoredAuthToken = () => localStorage.getItem(AUTH_TOKEN_KEY);

// Support cookie auth and bearer-token fallback for cross-domain deployments.
axios.defaults.withCredentials = true;
axios.interceptors.request.use((config) => {
  const token = getStoredAuthToken();
  if (token && !config.headers?.Authorization) {
    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${token}`,
    };
  }
  return config;
});

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

// Auth Provider
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      persistAuthToken(response.data?.token);
      setUser(response.data);
    } catch (error) {
      clearAuthToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    persistAuthToken(response.data?.token);
    setUser(response.data);
    return response.data;
  };

  const register = async (name, email, password) => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password, role: "client" }, { withCredentials: true });
    persistAuthToken(response.data?.token);
    setUser(response.data);
    return response.data;
  };

  const registerConsultant = async ({ name, email, password, profile }) => {
    const response = await axios.post(
      `${API}/auth/register`,
      { name, email, password, role: "consultant" },
      { withCredentials: true }
    );

    persistAuthToken(response.data?.token);
    setUser(response.data);

    await axios.post(`${API}/consultants`, profile, {
      withCredentials: true,
      headers: response.data?.token
        ? { Authorization: `Bearer ${response.data.token}` }
        : undefined,
    });

    return response.data;
  };

  const registerAccountant = async ({ name, email, password, profile }) => {
    const response = await axios.post(
      `${API}/auth/register`,
      { name, email, password, role: "accountant" },
      { withCredentials: true }
    );

    persistAuthToken(response.data?.token);
    setUser(response.data);

    await axios.post(`${API}/accountants/profile`, profile, {
      withCredentials: true,
      headers: response.data?.token
        ? { Authorization: `Bearer ${response.data.token}` }
        : undefined,
    });

    return response.data;
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    clearAuthToken();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, registerConsultant, registerAccountant, googleLogin, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await axios.post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true });
          persistAuthToken(response.data?.token);
          setUser(response.data);
          toast.success("Login realizado com sucesso!");
          
          // Redirect based on role
          const role = response.data.role;
          if (role === "admin") {
            navigate("/admin", { replace: true });
          } else if (role === "programmer") {
            navigate("/programmer/finance", { replace: true });
          } else if (role === "employee") {
            navigate("/admin/users", { replace: true });
          } else if (role === "consultant") {
            navigate("/consultant", { replace: true });
          } else if (role === "accountant") {
            navigate("/accountant", { replace: true });
          } else {
            navigate("/dashboard", { replace: true });
          }
        } catch (error) {
          console.error("Auth callback error:", error);
          toast.error("Erro ao processar login");
          navigate("/login", { replace: true });
        }
      } else {
        navigate("/login", { replace: true });
      }
    };

    processAuth();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy mx-auto mb-4"></div>
        <p className="text-slate-600">Processando autenticação...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard
    if (user.role === "admin") return <Navigate to="/admin" replace />;
    if (user.role === "programmer") return <Navigate to="/programmer/finance" replace />;
    if (user.role === "employee") return <Navigate to="/admin/users" replace />;
    if (user.role === "consultant") return <Navigate to="/consultant" replace />;
    if (user.role === "accountant") return <Navigate to="/accountant" replace />;
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Router
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/consultor-parceiro/cadastro" element={<ConsultantPartnerRegisterPage />} />
      <Route path="/contador-parceiro/cadastro" element={<AccountantPartnerRegisterPage />} />
      <Route path="/contador-parceiro/:token" element={<AccountantPartnerLandingPage />} />

      {/* Client routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute allowedRoles={["client", "admin"]}>
          <ClientDashboard />
        </ProtectedRoute>
      } />
      <Route path="/diagnostic/:diagnosticId" element={
        <ProtectedRoute allowedRoles={["client", "admin"]}>
          <DiagnosticWizard />
        </ProtectedRoute>
      } />
      <Route path="/results/:diagnosticId" element={
        <ProtectedRoute allowedRoles={["client", "admin", "consultant", "accountant"]}>
          <DiagnosticResults />
        </ProtectedRoute>
      } />
      <Route path="/payment/diagnostic/:diagnosticId" element={
        <ProtectedRoute allowedRoles={["client", "admin", "accountant"]}>
          <DiagnosticPaymentStatus />
        </ProtectedRoute>
      } />
      <Route path="/payment/diagnostic/:diagnosticId/:paymentState" element={
        <ProtectedRoute allowedRoles={["client", "admin", "accountant"]}>
          <DiagnosticPaymentStatus />
        </ProtectedRoute>
      } />

      {/* Admin routes */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={["admin", "programmer"]}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute allowedRoles={["admin", "employee", "programmer"]}>
          <AdminUsers />
        </ProtectedRoute>
      } />
      <Route path="/admin/coupons" element={
        <ProtectedRoute allowedRoles={["admin", "programmer"]}>
          <AdminCoupons />
        </ProtectedRoute>
      } />
      <Route path="/admin/consultants" element={
        <ProtectedRoute allowedRoles={["admin", "programmer"]}>
          <AdminConsultants />
        </ProtectedRoute>
      } />
      <Route path="/admin/accountants" element={
        <ProtectedRoute allowedRoles={["admin", "programmer"]}>
          <AdminAccountants />
        </ProtectedRoute>
      } />
      <Route path="/admin/referrals" element={
        <ProtectedRoute allowedRoles={["admin", "programmer"]}>
          <AdminReferrals />
        </ProtectedRoute>
      } />
      <Route path="/admin/consultancy-requests" element={
        <ProtectedRoute allowedRoles={["admin", "programmer"]}>
          <AdminConsultancyRequests />
        </ProtectedRoute>
      } />
      <Route path="/admin/companies" element={
        <ProtectedRoute allowedRoles={["admin", "programmer"]}>
          <AdminCompanies />
        </ProtectedRoute>
      } />
      <Route path="/admin/results/:diagnosticId" element={
        <ProtectedRoute allowedRoles={["admin", "programmer"]}>
          <AdminResults />
        </ProtectedRoute>
      } />

      {/* Programmer routes */}
      <Route path="/programmer/finance" element={
        <ProtectedRoute allowedRoles={["programmer"]}>
          <ProgrammerFinance />
        </ProtectedRoute>
      } />
      <Route path="/programmer/analytics" element={
        <ProtectedRoute allowedRoles={["programmer"]}>
          <ProgrammerAnalytics />
        </ProtectedRoute>
      } />
      <Route path="/programmer/audit" element={
        <ProtectedRoute allowedRoles={["programmer"]}>
          <ProgrammerAudit />
        </ProtectedRoute>
      } />

      {/* Consultant routes */}
      <Route path="/consultant" element={
        <ProtectedRoute allowedRoles={["consultant"]}>
          <ConsultantDashboard />
        </ProtectedRoute>
      } />
      <Route path="/accountant" element={
        <ProtectedRoute allowedRoles={["accountant"]}>
          <AccountantDashboard />
        </ProtectedRoute>
      } />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
